/**
 * MM Defense System — Action Recommendation Engine
 *
 * Maps trigger firings to recommended defensive actions.
 * Pure functions, no side effects. Domino plugs in optimized thresholds later.
 *
 * Priority cascade: lower number = higher priority.
 * Dedup: cancel_both_sides supersedes cancel_exposed_side for the same tickers.
 */

import type {
  TriggerFiring,
  TriggerSeverity,
  TriggerType,
  DefenseAction,
  DefensePosture,
  DefenseStatus,
  GameDefenseState,
  ActionConfig,
  ActionPriority,
} from './types';
import { DEFAULT_ACTION_CONFIG } from './types';

// ── Helpers ──

function severityRank(s: TriggerSeverity): number {
  return s === 'tier3' ? 3 : s === 'tier2' ? 2 : 1;
}

function hasTrigger(
  triggers: TriggerFiring[],
  type: TriggerType,
  minSeverity?: TriggerSeverity
): TriggerFiring | undefined {
  return triggers.find(
    (t) =>
      t.type === type && (!minSeverity || severityRank(t.severity) >= severityRank(minSeverity))
  );
}

function hasTriggerAny(triggers: TriggerFiring[], type: TriggerType): TriggerFiring | undefined {
  return triggers.find((t) => t.type === type);
}

function collectTickers(firings: (TriggerFiring | undefined)[]): string[] {
  const set = new Set<string>();
  for (const f of firings) {
    if (f) {
      for (const t of f.tickers) set.add(t);
    }
  }
  return [...set];
}

function countBySeverity(triggers: TriggerFiring[]): {
  tier1: number;
  tier2: number;
  tier3: number;
} {
  let tier1 = 0;
  let tier2 = 0;
  let tier3 = 0;
  for (const t of triggers) {
    if (t.severity === 'tier3') tier3++;
    else if (t.severity === 'tier2') tier2++;
    else tier1++;
  }
  return { tier1, tier2, tier3 };
}

// ── computePosture ──

/**
 * Determine the overall defense posture from active triggers.
 *
 * - red:    any tier3 firing
 * - orange: 2+ tier2 firings
 * - yellow: any tier2, or 3+ tier1
 * - green:  everything else
 */
export function computePosture(triggers: TriggerFiring[]): DefensePosture {
  const counts = countBySeverity(triggers);

  if (counts.tier3 > 0) return 'red';
  if (counts.tier2 >= 2) return 'orange';
  if (counts.tier2 >= 1 || counts.tier1 >= 3) return 'yellow';
  return 'green';
}

// ── recommendActions ──

/**
 * Evaluate triggers and generate a prioritized, deduplicated list of defensive actions.
 *
 * Rules are evaluated in priority order. cancel_both_sides supersedes
 * cancel_exposed_side for the same tickers.
 */
export function recommendActions(
  triggers: TriggerFiring[],
  config?: Partial<ActionConfig>,
  _nowMs?: number
): DefenseAction[] {
  const cfg: ActionConfig = { ...DEFAULT_ACTION_CONFIG, ...config };
  const actions: DefenseAction[] = [];

  // Rule 1: correlated_market_move (any severity) → cancel_both_sides
  const correlatedMove = hasTriggerAny(triggers, 'correlated_market_move');
  if (correlatedMove) {
    actions.push({
      type: 'cancel_both_sides',
      priority: 1 as ActionPriority,
      description: 'Cancel all orders — correlated market move detected',
      causedBy: ['correlated_market_move'],
      params: { type: 'cancel_both_sides', tickers: collectTickers([correlatedMove]) },
    });
  }

  // Rule 2: unpaired_fill_timer (tier3) + post_fill_drift (tier2+) → cancel_exposed + cooldown
  const unpairedT3 = hasTrigger(triggers, 'unpaired_fill_timer', 'tier3');
  const driftT2Plus = hasTrigger(triggers, 'post_fill_drift', 'tier2');
  if (unpairedT3 && driftT2Plus) {
    const tickers = collectTickers([unpairedT3, driftT2Plus]);
    actions.push({
      type: 'cancel_exposed_side',
      priority: 1 as ActionPriority,
      description: 'Cancel exposed side — unpaired fill timed out with adverse drift',
      causedBy: ['unpaired_fill_timer', 'post_fill_drift'],
      params: { type: 'cancel_exposed_side', tickers, side: 'both' },
    });
    actions.push({
      type: 'cooldown',
      priority: 2 as ActionPriority,
      description: 'Enter cooldown — unpaired fill timed out with adverse drift',
      causedBy: ['unpaired_fill_timer', 'post_fill_drift'],
      params: { type: 'cooldown', tickers, durationMs: cfg.cooldownDurationMs },
    });
  }

  // Rule 3: post_fill_drift (tier3) → cancel_exposed + cross_venue_hedge
  const driftT3 = hasTrigger(triggers, 'post_fill_drift', 'tier3');
  if (driftT3) {
    const tickers = collectTickers([driftT3]);
    actions.push({
      type: 'cancel_exposed_side',
      priority: 1 as ActionPriority,
      description: 'Cancel exposed side — severe post-fill drift',
      causedBy: ['post_fill_drift'],
      params: { type: 'cancel_exposed_side', tickers, side: 'both' },
    });
    actions.push({
      type: 'cross_venue_hedge',
      priority: 2 as ActionPriority,
      description: 'Hedge across venue — severe post-fill drift',
      causedBy: ['post_fill_drift'],
      params: { type: 'cross_venue_hedge', tickers, hedgeVenue: cfg.hedgeVenue },
    });
  }

  // Rule 4: post_fill_drift (tier2) → dynamic_retreat
  const driftT2 = hasTrigger(triggers, 'post_fill_drift', 'tier2');
  if (driftT2 && !driftT3) {
    // Only if not already escalated to tier3
    const tickers = collectTickers([driftT2]);
    actions.push({
      type: 'dynamic_retreat',
      priority: 3 as ActionPriority,
      description: 'Retreat quotes — moderate post-fill drift',
      causedBy: ['post_fill_drift'],
      params: { type: 'dynamic_retreat', tickers, retreatTicks: cfg.retreatTicks },
    });
  }

  // Rule 5: volume_spike (tier2+) + one_sided_volume (any) → size_reduction + dynamic_retreat
  const volumeSpike = hasTrigger(triggers, 'volume_spike', 'tier2');
  const oneSided = hasTriggerAny(triggers, 'one_sided_volume');
  if (volumeSpike && oneSided) {
    const tickers = collectTickers([volumeSpike, oneSided]);
    actions.push({
      type: 'size_reduction',
      priority: 2 as ActionPriority,
      description: 'Reduce size — volume spike with one-sided flow',
      causedBy: ['volume_spike', 'one_sided_volume'],
      params: { type: 'size_reduction', tickers, reductionFactor: cfg.sizeReductionFactor },
    });
    actions.push({
      type: 'dynamic_retreat',
      priority: 3 as ActionPriority,
      description: 'Retreat quotes — volume spike with one-sided flow',
      causedBy: ['volume_spike', 'one_sided_volume'],
      params: { type: 'dynamic_retreat', tickers, retreatTicks: cfg.retreatTicks },
    });
  }

  // Rule 6: spread_blowout (tier2+) → dynamic_retreat
  const spreadBlowout = hasTrigger(triggers, 'spread_blowout', 'tier2');
  if (spreadBlowout) {
    const tickers = collectTickers([spreadBlowout]);
    actions.push({
      type: 'dynamic_retreat',
      priority: 3 as ActionPriority,
      description: 'Retreat quotes — spread blowout detected',
      causedBy: ['spread_blowout'],
      params: { type: 'dynamic_retreat', tickers, retreatTicks: cfg.retreatTicks },
    });
  }

  // Rule 7: stale_quote (tier2+) → cancel_both_sides
  const staleQuote = hasTrigger(triggers, 'stale_quote', 'tier2');
  if (staleQuote) {
    actions.push({
      type: 'cancel_both_sides',
      priority: 1 as ActionPriority,
      description: 'Cancel all — stale quotes, no reliable pricing',
      causedBy: ['stale_quote'],
      params: { type: 'cancel_both_sides', tickers: collectTickers([staleQuote]) },
    });
  }

  // Rule 8: exposure_breach (tier2+) → cancel_exposed + size_reduction
  const exposureBreach = hasTrigger(triggers, 'exposure_breach', 'tier2');
  if (exposureBreach) {
    const tickers = collectTickers([exposureBreach]);
    actions.push({
      type: 'cancel_exposed_side',
      priority: 2 as ActionPriority,
      description: 'Cancel exposed side — exposure limit breached',
      causedBy: ['exposure_breach'],
      params: { type: 'cancel_exposed_side', tickers, side: 'both' },
    });
    actions.push({
      type: 'size_reduction',
      priority: 3 as ActionPriority,
      description: 'Reduce size — exposure limit breached',
      causedBy: ['exposure_breach'],
      params: { type: 'size_reduction', tickers, reductionFactor: cfg.sizeReductionFactor },
    });
  }

  // ── Dedup: cancel_both_sides supersedes cancel_exposed_side for same tickers ──
  const cancelBothTickers = new Set<string>();
  for (const a of actions) {
    if (a.type === 'cancel_both_sides' && a.params.type === 'cancel_both_sides') {
      for (const t of a.params.tickers) cancelBothTickers.add(t);
    }
  }

  const deduped = actions.filter((a) => {
    if (a.type !== 'cancel_exposed_side') return true;
    if (a.params.type !== 'cancel_exposed_side') return true;
    // Remove if ALL tickers are already covered by cancel_both_sides
    const allCovered = a.params.tickers.every((t) => cancelBothTickers.has(t));
    return !allCovered;
  });

  // Sort by priority ascending (1 = highest priority)
  deduped.sort((a, b) => a.priority - b.priority);

  return deduped;
}

// ── buildDefenseStatus ──

/**
 * Build the complete defense status: posture, triggers, actions, and per-game breakdown.
 */
export function buildDefenseStatus(
  triggers: TriggerFiring[],
  config?: Partial<ActionConfig>,
  nowMs?: number
): DefenseStatus {
  const evalMs = nowMs ?? Date.now();
  const posture = computePosture(triggers);
  const actions = recommendActions(triggers, config, evalMs);
  const triggerCount = countBySeverity(triggers);

  // Build byGame map: group triggers by ticker, compute per-game posture
  const byGame = new Map<string, GameDefenseState>();

  for (const trigger of triggers) {
    for (const ticker of trigger.tickers) {
      if (!byGame.has(ticker)) {
        byGame.set(ticker, { posture: 'green', triggerCount: 0 });
      }
    }
  }

  // Group triggers per game
  const triggersPerGame = new Map<string, TriggerFiring[]>();
  for (const trigger of triggers) {
    for (const ticker of trigger.tickers) {
      const existing = triggersPerGame.get(ticker) ?? [];
      existing.push(trigger);
      triggersPerGame.set(ticker, existing);
    }
  }

  // Compute per-game posture
  for (const [ticker, gameTriggers] of triggersPerGame) {
    byGame.set(ticker, {
      posture: computePosture(gameTriggers),
      triggerCount: gameTriggers.length,
    });
  }

  return {
    posture,
    activeTriggers: triggers,
    recommendedActions: actions,
    triggerCount,
    byGame,
    evaluatedAtMs: evalMs,
  };
}

// ── formatDefenseSummary ──

/**
 * Format a human-readable defense status summary.
 *
 * Example: "RED — 3 triggers (1 tier3, 2 tier2) — 2 actions recommended"
 */
export function formatDefenseSummary(status: DefenseStatus): string {
  const { posture, triggerCount, recommendedActions, byGame } = status;
  const totalTriggers = triggerCount.tier1 + triggerCount.tier2 + triggerCount.tier3;

  // Build severity breakdown
  const parts: string[] = [];
  if (triggerCount.tier3 > 0) parts.push(`${triggerCount.tier3} tier3`);
  if (triggerCount.tier2 > 0) parts.push(`${triggerCount.tier2} tier2`);
  if (triggerCount.tier1 > 0) parts.push(`${triggerCount.tier1} tier1`);

  const severityStr = parts.length > 0 ? ` (${parts.join(', ')})` : '';
  const triggerStr = `${totalTriggers} trigger${totalTriggers !== 1 ? 's' : ''}`;
  const actionStr = `${recommendedActions.length} action${recommendedActions.length !== 1 ? 's' : ''} recommended`;

  let summary = `${posture.toUpperCase()} — ${triggerStr}${severityStr} — ${actionStr}`;

  // Per-game breakdown if multiple games
  if (byGame.size > 1) {
    const gameLines: string[] = [];
    for (const [ticker, state] of byGame) {
      gameLines.push(
        `  ${ticker}: ${state.posture.toUpperCase()} (${state.triggerCount} trigger${state.triggerCount !== 1 ? 's' : ''})`
      );
    }
    summary += '\n' + gameLines.join('\n');
  }

  return summary;
}
